import { useMemo } from 'react'
import { Button, Icon, LoadingSpinner } from '../atoms'
import type { POICandidate } from '../../types'
import { haversineMeters } from '../../utils/geo'

export interface POIListProps {
  pois: Array<POICandidate & { distance?: number }>
  userPosition: { lat: number; lon: number } | null
  onPOISelect: (poi: POICandidate) => void
  onRoute: (poi: POICandidate) => void
  onNarrate?: (poi: POICandidate) => void
  onStartTracking?: () => void
  isTracking?: boolean
  onPinLocation?: () => void
  narratingPOI?: string | null
  narrationError?: string | null
  isLoading?: boolean
}

export const POIList = ({
  pois,
  userPosition,
  onPOISelect,
  onRoute,
  onNarrate,
  onStartTracking,
  isTracking = false,
  onPinLocation,
  narratingPOI = null,
  narrationError = null,
  isLoading = false,
}: POIListProps) => {
  const sortedPOIs = useMemo(() => {
    const poisWithDistance = pois.map((poi) => {
      const distance = userPosition
        ? haversineMeters(userPosition.lat, userPosition.lon, poi.lat, poi.lon)
        : null
      return { ...poi, distance }
    })

    return poisWithDistance.sort((a, b) => {
      if (a.distance == null) return 1
      if (b.distance == null) return -1
      return a.distance - b.distance
    })
  }, [pois, userPosition])

  if (sortedPOIs.length === 0) {
    if (isLoading) {
      return (
        <div className="text-text-muted text-sm text-center py-6">
          <LoadingSpinner className="w-10 h-10 mx-auto mb-3" />
          <p className="font-medium mb-2">Loading points of interest...</p>
          <p className="text-xs">Discovering interesting locations around you</p>
        </div>
      )
    }

    return (
      <div className="text-text-muted text-sm text-center py-6">
        <Icon name="location" className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p className="font-medium mb-2">No points of interest found yet</p>
        <p className="text-xs mb-4">Discover interesting locations around you</p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          {!isTracking && onStartTracking && (
            <Button
              onClick={onStartTracking}
              variant="primary"
              className="min-h-[44px] touch-manipulation"
            >
              Start Tracking
            </Button>
          )}
          {onPinLocation && (
            <Button
              onClick={onPinLocation}
              variant="secondary"
              className="min-h-[44px] touch-manipulation"
            >
              Pin Location
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto">
      {sortedPOIs.map((poi) => (
        <div
          key={poi.key}
          className="p-4 border border-border rounded-xl hover:bg-background-elevated hover:border-primary/50 transition-all cursor-pointer active:scale-[0.98]"
          onClick={() => onPOISelect(poi)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-base mb-1.5 line-clamp-2">{poi.title}</div>
              <div className="flex items-center gap-3 flex-wrap text-sm">
                {poi.distance != null && (
                  <div className="flex items-center gap-1 text-primary font-medium">
                    <Icon name="location" className="w-4 h-4" />
                    <span>
                      {poi.distance >= 1000
                        ? `${(poi.distance / 1000).toFixed(2)} km`
                        : `${Math.round(poi.distance)} m`}
                    </span>
                  </div>
                )}
                <span className="px-2 py-0.5 bg-background-elevated rounded text-xs text-text-muted capitalize">
                  {poi.source === 'wiki' ? 'Wikipedia' : poi.source === 'osm' ? 'OSM' : poi.source === 'wdnature' ? 'Wikidata' : 'Google'}
                </span>
                {poi.rating && (
                  <span className="flex items-center gap-1 text-text">
                    <span>⭐</span>
                    <span className="font-medium">{poi.rating.toFixed(1)}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {onNarrate && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (narratingPOI !== poi.key) {
                      onNarrate(poi)
                    }
                  }}
                  variant="secondary"
                  size="sm"
                  className="flex-shrink-0 flex items-center justify-center relative"
                  title={
                    narratingPOI === poi.key
                      ? "Loading..."
                      : narrationError && narratingPOI === poi.key
                      ? "Error loading"
                      : "Hear information about this POI"
                  }
                  aria-label="Narrate information about this point of interest"
                  disabled={narratingPOI === poi.key}
                >
                  {narratingPOI === poi.key ? (
                    <LoadingSpinner className="w-4 h-4" />
                  ) : (
                    <Icon name="volume" className="w-4 h-4" />
                  )}
                </Button>
              )}
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  onRoute(poi)
                }}
                variant="primary"
                size="sm"
                className="flex-shrink-0 flex items-center justify-center"
                title="Plan route to this POI"
                aria-label="Plan route to this point of interest"
              >
                <Icon name="route" className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
